import type { z } from "zod";

const error = (): z.core.$ZodErrorMap => {
  const Sizable = {
    string: { unit: "character", pluralUnit: "characters" },
    file: { unit: "byte", pluralUnit: "bytes" },
    array: { unit: "element", pluralUnit: "elements" },
    set: { unit: "item", pluralUnit: "items" },
  };

  function getSizing(origin: string) {
    return Sizable[origin as keyof typeof Sizable] ?? null;
  }

  const parsedType = (data: unknown): string => {
    const t = typeof data;
    switch (t) {
      case "number": {
        return Number.isNaN(data) ? "NaN" : "number";
      }
      case "object": {
        if (Array.isArray(data)) {
          return "array";
        }
        if (data === null) {
          return "null";
        }
        if (Object.getPrototypeOf(data) !== Object.prototype && (data as { constructor?: { name: string } }).constructor) {
          return (data as { constructor: { name: string } }).constructor.name;
        }
      }
    }
    return t;
  };

  const Nouns: Record<string, string> = {
    regex: "input",
    email: "email address",
    url: "URL",
    emoji: "emoji",
    uuid: "UUID",
    uuidv4: "UUIDv4",
    uuidv6: "UUIDv6",
    nanoid: "nanoid",
    guid: "GUID",
    cuid: "cuid",
    cuid2: "cuid2",
    ulid: "ULID",
    xid: "XID",
    ksuid: "KSUID",
    datetime: "datetime",
    date: "date",
    time: "time",
    duration: "duration",
    ipv4: "IPv4 address",
    ipv6: "IPv6 address",
    cidrv4: "IPv4 range",
    cidrv6: "IPv6 range",
    base64: "base64 string",
    base64url: "base64url string",
    json_string: "JSON string",
    e164: "E.164 number",
    jwt: "JWT",
    template_literal: "input",
  };

  return (issue) => {
    switch (issue.code) {
      case "invalid_type":
        // Handle required fields (undefined, null, or empty string)
        if (issue.input === undefined || issue.input === null || issue.input === "") {
          return "Required";
        }

        // Handle type mismatches with actual values
        if (issue.expected === "number") {
          return "Expected a number";
        }
        if (issue.expected === "boolean") {
          return "Expected a boolean";
        }
        if (issue.expected === "date") {
          return "Expected a date";
        }

        return `Expected ${issue.expected}, received ${parsedType(issue.input)}`;

      case "invalid_value": {
        const values = issue.values.map((v) => String(v));
        if (values.length === 1) {
          return `Invalid input: expected ${values[0]}`;
        }
        return `Invalid value. Expected ${values.join(" | ")}`;
      }

      case "too_big": {
        const sizing = getSizing(issue.origin);
        if (sizing) {
          const unit = Number(issue.maximum) === 1 ? sizing.unit : sizing.pluralUnit;
          if (issue.exact) {
            return `Must contain exactly ${issue.maximum} ${unit}`;
          }
          return `Must contain at most ${issue.maximum} ${unit}`;
        }
        if (issue.inclusive) {
          return `Number must be less than or equal to ${issue.maximum}`;
        }
        return `Number must be less than ${issue.maximum}`;
      }

      case "too_small": {
        const sizing = getSizing(issue.origin);
        if (sizing) {
          const unit = Number(issue.minimum) === 1 ? sizing.unit : sizing.pluralUnit;
          if (issue.exact) {
            return `Must contain exactly ${issue.minimum} ${unit}`;
          }
          return `Must contain at least ${issue.minimum} ${unit}`;
        }
        if (issue.inclusive) {
          return `Number must be greater than or equal to ${issue.minimum}`;
        }
        return `Number must be greater than ${issue.minimum}`;
      }

      case "invalid_format": {
        const _issue = issue as typeof issue & { format?: string; prefix?: string; suffix?: string; includes?: string };
        if (_issue.format === "starts_with") {
          return `Must start with "${_issue.prefix}"`;
        }
        if (_issue.format === "ends_with") {
          return `Must end with "${_issue.suffix}"`;
        }
        if (_issue.format === "includes") {
          return `Must include "${_issue.includes}"`;
        }
        if (_issue.format === "regex") {
          return "Invalid format";
        }
        if (_issue.format === "email") {
          return "Invalid email address";
        }
        if (_issue.format === "url") {
          return "Invalid URL";
        }
        return `Invalid ${Nouns[_issue.format] ?? _issue.format}`;
      }

      case "not_multiple_of":
        return `Number must be a multiple of ${issue.divisor}`;

      case "unrecognized_keys":
        return `Unrecognized key${issue.keys.length > 1 ? "s" : ""}: ${issue.keys.join(", ")}`;

      case "invalid_key":
        return `Invalid key in ${issue.origin}`;

      case "invalid_union":
        return "Invalid input";

      case "invalid_element":
        return `Invalid value in ${issue.origin}`;

      default:
        return "Invalid input";
    }
  };
};

export default function () {
  return {
    localeError: error(),
  };
}
